<template>
  <div ref="calc" class="crop-calculator" :style="calcStyle">
    <!--Left Side-->
    <div class="crop-calculator__calculator text-center" :style="inputStyle">
      <!--Formula Select-->
      <div class="crop-calculator__no-border formula-wrapper print-hide">
        <AppHeading theme="black" spacing="lg" size="xs" class="calc-section crop-calculator-heading">
          Select Formula
        </AppHeading>
        <div class="formula-list">
          <router-link class="formula-button" to="/performance-resource-kit/s7gd634ghfdw6c/tools/me/retail-value-calculator/mesz">
            <div class="formula-icon formula-icon--border">
              <IconSZ />
            </div>
          </router-link>
           <router-link class="formula-button is-selected" to="/performance-resource-kit/s7gd634ghfdw6c/tools/me/retail-value-calculator/mes10">
            <div class="formula-icon formula-icon--border">
              <IconS10 />
            </div>
          </router-link>
          <router-link class="formula-button" to="/performance-resource-kit/s7gd634ghfdw6c/tools/me/retail-value-calculator/mes15">
            <div class="formula-icon formula-icon--border">
              <IconS15 />
            </div>
          </router-link>
        </div>
      </div>

      <!--Crop Select-->
      <div class="crop-calculator__options print-hide">
        <AppHeading theme="black" spacing="lg" size="xs" class="calc-section crop-calculator-heading">
          Select Your Crop
        </AppHeading>
        <CropList v-model="selectedCropValue" :crops="cropNames" />
      </div>

      <div class="crop-calculator__units print-hide">
        <AppHeading theme="black" spacing="lg" size="xs" class="calc-section crop-calculator-heading">
          Yield Unit of Measure
        </AppHeading>
        <div class="crop-calculator__bubble_button_wrapper desk-width print-hide">
          <label
            v-for="option in weightOptions"
            :key="option"
            class="crop-calculator__bubble_button crop-calculator__bubble_button--unit"
            :class="{
              'crop-calculator__bubble_button--selected': form.weightOption === option
            }"
          >
            <TextContent :content="option" />
            <input v-model="form.weightOption" :value="option" type="radio" />
          </label>
        </div>
      </div>

      <div class="crop-calculator__input crop-calculator__input--price crop-calculator__border-bottom text-center">
        <AppHeading element="h2" size="xs" spacing="lg" class="calc-section crop-calculator-heading">
          Enter local retail price of {{ selectedCropValue }}
        </AppHeading>
        <div class="crop-calculator__numeric">
          <span class="prepend">$</span>
            <input
              v-model="form.retailPrice"
              class="numeric-input price"
              :min="0"
              :max="100"
              placeholder="0.00"
              maxlength="4"
              type="number"
              :step="selectedCrop.priceInterval"
              @blur="handlePriceBlur"
            />
            <small>/ <TextContent :content="form.weightOption.split('/')[0]"/></small>
        </div>
      </div>

      <div class="print-hide">
        <br />
        <AppHeading theme="black" element="h2" size="xs" spacing="lg" class="calc-section crop-calculator-heading">
          Current Blend Phosphorus Source
        </AppHeading>
        <div class="crop-calculator__bubble_button_wrapper centered-wrapper print-hide">
          <label
            v-for="fertilizer in fertilizers"
            :key="fertilizer.id"
            class="crop-calculator__bubble_button"
            :class="{
              'crop-calculator__bubble_button--long': fertilizer.name.length > 3,
              'crop-calculator__bubble_button--selected': isFertilizerSelected(fertilizer.id)
            }"
          >
            <TextContent :content="fertilizer.name" />
            <input v-model="selectedFertilizerId" :value="fertilizer.id" type="radio" />
          </label>
        </div>
        <br />

        <AppHeading theme="black" element="h2" size="xs" spacing="lg" class="calc-section crop-calculator-heading">
          Fertilizer Unit of Measure
        </AppHeading>
        <div class="crop-calculator__bubble_button_wrapper desk-width print-hide">
          <label
            v-for="unit in unitsOfMeasure"
            :key="unit.id"
            class="crop-calculator__bubble_button crop-calculator__bubble_button--unit bubble-button-fertilizer"
            :class="{
              'crop-calculator__bubble_button--selected': form.unitOfMeasure === unit.name
            }"
          >
            <TextContent :content="unit.name" />
            <input v-model="form.unitOfMeasure" :value="unit.name" type="radio" />
          </label>
        </div>
        <br />
        <div class="crop-calculator__input crop-calculator__border-bottom">
          <AppHeading element="h2" size="xs" spacing="lg" class="calc-section crop-calculator-heading">
            Do you balance for nitrogen?
          </AppHeading>
          <div class="crop-calculator__bubble_button_wrapper short-wrapper centered-wrapper">
            <label
              v-for="balance in [true, false]"
              :key="balance"
              class="crop-calculator__bubble_button"
              :class="{
                'crop-calculator__bubble_button--selected': form.balanceNitrogen === balance
              }"
            >
              <TextContent :content="balance ? 'YES' : 'NO'" />
              <input v-model="form.balanceNitrogen" :value="balance" type="radio" />
            </label>
          </div>
          <AppButton
            class="crop-calculator__sample-blend--button print-hide"
            @click="toggleNitrogenInformation()"
          >
            <AppParagraph size="md">
              View additional information on Balancing for Nitrogen
            </AppParagraph>
          </AppButton>

          <ContentModal :isOpen="isNitrogenSummaryOpen" :close="toggleNitrogenInformation" :headline="nitrogenTitle">
            <div class="crop-calculator__sample-blend--container print-hide">
              <AppHeading element="h3" spacing="md" size="sm">
                Why Balance for Nitrogen?
              </AppHeading>
              <AppParagraph size="sm" spacing="xl">
                Depending on the components selected, balancing for Nitrogen will cause the calculator to add Urea to
                <span v-if="performanceProductFormula.name === productFormulaMSZ">{{ productFormulaMSZ }}</span>
                <span v-else-if="performanceProductFormula.name === productFormulaMS10">{{ productFormulaMS10 }}</span>
                <span v-else>{{ productFormulaMS15 }}</span> or the MAP/DAP blend, creating a balanced price analysis for an
                equivalent rate of nitrogen in both treatments.
              </AppParagraph>
              <AppHeading element="h3" spacing="md" size="sm">
                Fall vs. Spring Applications
              </AppHeading>
              <AppParagraph size="sm" spacing="xl">
                The risk of Nitrogen loss over the Fall from leaching or denitrification may actually decrease the value of
                the higher Nitrogen treatment. Therefore, during Fall applications, the user may not want to balance for
                Nitrogen.
              </AppParagraph>
            </div>
          </ContentModal>
        </div>

        <AppHeading theme="black" size="md" spacing="lg" class="large-heading">
          Blend Comparison
        </AppHeading>
        <div class="crop-calculator__input crop-calculator__input--price no-padding">
          <AppHeading element="h2" size="xs" spacing="lg" class="calc-section crop-calculator-heading">
            Blend's Rate of P<sub>2</sub>O<sub>5</sub>
          </AppHeading>
          <div class="crop-calculator__numeric">
            <input
              v-model="phosAppliedValue"
              class="numeric-input"
              :min="0"
              :max="100"
              maxlength="4"
              placeholder="0.00"
              :step="selectedCrop.priceInterval"
              type="number"
              @blur="handleSulfateBlendBlur"
            />
            <small>
              <TextContent :content="setWeightOption.split('/')[0]" />
              /
              <TextContent :content="setWeightOption.split('/')[1]" />
            </small>
          </div>
          <AppButton class="crop-calculator__sample-blend--button print-hide" @click="openSampleBlend()">
            <AppParagraph size="md">
              Sample calculations to help
            </AppParagraph>
          </AppButton>

          <ContentModal :isOpen="isSampleBlendOpen" :close="closeSampleBlend" :headline="sampleBlendTitle">
            <div class="crop-calculator__sample-blend--container print-hide">
              <table class="crop-calculator__sample-blend--table" align="center">
                <thead>
                  <tr class="crop-calculator__sample-blend--header">
                    <td align="center" colspan="20%" class="blend-title">
                      <AppParagraph size="md">
                        <strong>Product</strong>
                      </AppParagraph>
                    </td>
                    <td align="center" colspan="37%" class="blend-title">
                      <AppParagraph size="md">
                        <strong>Rate of P<sub>2</sub>O<sub>5</sub></strong>
                      </AppParagraph>
                      <AppParagraph size="sm">(lbs per acre)</AppParagraph>
                    </td>
                    <td align="center" colspan="37%" class="blend-title">
                      <AppParagraph size="md">
                        <strong>Amount of Product</strong>
                      </AppParagraph>
                      <AppParagraph size="sm">(lbs per acre)</AppParagraph>
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr class="crop-calculator__sample-blend--row">
                    <td align="center" colspan="20%" class="blend-item">
                      <span>DAP 18-46-0</span>
                    </td>
                    <td align="center" colspan="37%" class="blend-item">
                      <strong>60</strong>
                    </td>
                    <td align="center" colspan="37%" class="blend-item">
                      <span>130</span>
                    </td>
                  </tr>
                  <tr class="crop-calculator__sample-blend--row">
                    <td align="center" colspan="20%" class="blend-item">
                      <span>MAP 11-52-0</span>
                    </td>
                    <td align="center" colspan="37%" class="blend-item">
                      <strong>60</strong>
                    </td>
                    <td align="center" colspan="37%" class="blend-item">
                      <span>115</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ContentModal>
        </div>
        <br />

        <div class="crop-calculator__input crop-calculator__input--price no-padding border-bottom-st-sf">
          <AppHeading spacing="lg" size="xs" class="calc-section crop-calculator-heading">
            Blend's Sulfur Source
          </AppHeading>
          <div class="crop-calculator__bubble_button_wrapper short-wrapper">
            <label
              v-for="blend in ammoniumSulfateBlends"
              :key="blend.name"
              class="crop-calculator__bubble_button"
              :class="{
                'crop-calculator__bubble_button--long': blend.name.length > 3,
                'crop-calculator__bubble_button--selected': form.sulfurBlend === blend.value
              }"
            >
              <TextContent :content="blend.name" />
              <input v-model="form.sulfurBlend" :value="blend.value" type="radio" />
            </label>
          </div>
          <AppButton class="crop-calculator__view-btn print-hide" @click="openNutrientSummary()">
            View Nutrient Summary
          </AppButton>
        </div>

        <AppHeading theme="black" element="h3" size="md" spacing="lg" class="large-heading">
          Local Retail Prices
        </AppHeading>
        <LocalRetailPriceInput
          v-model="form.localRetailPrice.cropnutrition"
          title="MicroEssentials"
          :unit="form.unitOfMeasure"
          class="calc-section crop-calculator-heading"
        />

        <LocalRetailPriceInput
          v-model="form.localRetailPrice.currentBlend"
          :title="selectedFertilizer.name"
          :unit="form.unitOfMeasure"
          class="calc-section crop-calculator-heading"
        />

        <LocalRetailPriceInput
          v-if="form.sulfurBlend === 'percent_100_ammonium_sulfate' || form.sulfurBlend === 'percent_50_elemental_sulfur'"
          :key="form.sulfurBlend"
          v-model="form.localRetailPrice.ammoniumSulfate"
          title="Ammonium Sulfate (AMS)"
          :unit="form.unitOfMeasure"
          class="calc-section crop-calculator-heading"
        />

        <LocalRetailPriceInput
          v-if="form.sulfurBlend === 'percent_100_elemental_sulfur' || form.sulfurBlend === 'percent_50_elemental_sulfur'"
          :key="form.sulfurBlend"
          v-model="form.localRetailPrice.elementalSulfur"
          title="ELEMENTAL SULFUR (0-0-0-90S)"
          :unit="form.unitOfMeasure"
          class="calc-section crop-calculator-heading"
        />

        <LocalRetailPriceInput
          v-if="form.balanceNitrogen"
          v-model="form.localRetailPrice.urea"
          title="Urea"
          :unit="form.unitOfMeasure"
          class="calc-section crop-calculator-heading"
        />

        
        <br />
        <br />
        <AppButton class="crop-calculator__calc-btn calculate-button" icon="ArrowRight" @keydown.tab="showResults()" @click="showResults()">
          Calculate Your Results
        </AppButton>
      </div>
    </div>
    <!--End Left Side-->

    <!--Right Side-->
    <div ref="roi" class="crop-calculator__roi" :style="roiStyle">
      <AppButton class="crop-calculator__return-btn print-hide" icon="ArrowRight" @click="showInputs()">
        Go back to change inputs
      </AppButton>
      <AppHeading element="h1" size="sm" spacing="md" theme="black" class="crop-calculator__results-heading print-hide crop-calculator-result-value-heading">
        Value Calculator
      </AppHeading>
      <div class="print-wrapper">
        <div class="print print-left">
          <!-- <PrintDetail
            :performanceProductFormula="performanceProductFormula.slug"
            :rateOfPhosphorusApplied="form.rateOfPhosphorusApplied || 0"
            :cropYield="form.yield"
            :selectedCrop="selectedCropValue"
            :selectedFertilizer="selectedFertilizer"
            :retailPrice="form.retailPrice"
            :selectedAmmoniumSulfateBlend="
              blends.ammoniumSulfateBlend[form.sulfurBlend] && blends.ammoniumSulfateBlend[form.sulfurBlend]
            "
            :weightOption="form.weightOption"
          /> -->
          <PrintPersonalization 
            :date="getTodaysDate()" 
            :preparedFor="form.personalization.preparedFor"
            :preparedBy="form.personalization.preparedBy"
            :company="form.personalization.company"
            :phoneNumber="form.personalization.phoneNumber"
            :cropSelected="selectedCropValue"
            :retailPrice="form.retailPrice"
            :roiIncrease="roiIncrease"
            :rateOfPhosphorusApplied="calculatorResults.rateOfPhosphorusApplied"
            :weightOption="setWeightOption"
            :currentBlendPDF="currentBlendPDF()"
          />
        </div>
        <div class="print-right">
          <div class="print-detail-right">
            <AppHeading theme="black" spacing="xs" size="xl" class="crop-calculator__results-large-heading print-hide crop-calculator-result-heading">
              Your Results
            </AppHeading>
            <CircularProgress :value="chartValue">
              <img :src="logo" :alt="name" class="calc-logo" />
              <AppHeading element="h3" size="sm" spacing="md" class="crop-calculator-heading">
                ROI increase
              </AppHeading>

              <div class="roi-increase">
                <div class="roi-increase--value">
                  <AppHeading element="h4" size="lg" spacing="xs" theme="primary">
                    $<TextContent :content="roiIncrease" />
                  </AppHeading>
                </div>

                <div class="roi-increase--weight">
                  <AppHeading element="h3" size="sm"> / <TextContent :content="form.weightOption.split('/')[1]" /> </AppHeading>
                </div>
              </div>
            </CircularProgress>

            <div class="crop-results-projected-revenue">
              <AppHeading theme="black" spacing="xs" size="xl" class="crop-calculator__results-large-heading crop-calculator-print-heading print crop-calculator-result-heading">
                Projected Revenue
              </AppHeading>
            
              <AppFlexRow>
                <AppFlexCol
                  class="crop-calculator__add-col crop-calculator__add-col--bordered crop-calculator__remove-col--bordered-top crop-calculator__additional-totals"
                >
                  <AppHeading theme="black" element="h5" size="xs" spacing="sm" class="crop-calculator-additional-heading">
                    Additional revenue
                  </AppHeading>
                  <AppHeading element="h4" size="sm" theme="primary">
                    <div class="crop-calculator__additional-revenue">
                      <TextContent :content="additionalRevenueValue" />
                      <small class="black"> / <TextContent :content="form.weightOption.split('/')[1]" /> </small>
                    </div>
                  </AppHeading>
                </AppFlexCol>

                <AppFlexCol
                  class="crop-calculator__add-col crop-calculator__add-col--bordered crop-calculator__remove-col--bordered-top crop-calculator__additional-totals"
                >
                  <AppHeading theme="black" element="h5" size="xs" spacing="sm" class="crop-calculator-additional-heading">
                    Additional investment
                  </AppHeading>
                  <AppHeading element="h4" size="sm" theme="primary">
                    <div class="crop-calculator__additional-investment">
                      <TextContent :content="additionalInvestmentValue" />
                      <small class="black"> / <TextContent :content="form.weightOption.split('/')[1]" /> </small>
                    </div>
                  </AppHeading>
                </AppFlexCol>
              </AppFlexRow>
            </div>

            <div class="crop-calculator__input--price">
              <AppHeading theme="black" element="h2" size="xs" spacing="lg">
                <TextContent :content="performanceProductFormula.name" class="calc-section crop-calculator-heading" />
                <TextContent content=" Expected Yield Advantage" class="calc-section crop-calculator-heading" />
              </AppHeading>

              <div class="crop-calculator__update-yield-wrapper">
                <AppHeading element="h4" size="md">
                  <TextContent :content="formYield" class="growler-text" />
                </AppHeading>
                  <small class="smaller growler-unit">
                    <TextContent :content="form.weightOption.split('/')[0]" />
                    /
                    <TextContent :content="form.weightOption.split('/')[1]" />
                  </small>
              </div>
              <div class="value-calculator slider">
                <VueSlider
                  v-model="formYield"
                  :dotSize="24"
                  :interval="selectedCrop.priceInterval"
                  :height="2"
                  :min="minimumYield"
                  :max="maximumYield"
                  :silent="true"
                  tooltip="none"
                />
              </div>

              <div class="print-hide">
                <div class="crop-calculator__border-bottom-dotted medium-margin">
                  <div class="button-text-wrapper print-hide">
                    <AppParagraph theme="gray" size="sm" class="adjust-advantage">
                      Adjust the yield advantage to affect ROI
                    </AppParagraph>
                  </div>
                  <AppButton class="crop-calculator__reset-btn" @click="resetYield()">
                    Reset Yield Advantage
                  </AppButton>
                  <AppParagraph size="xs" theme="gray" class="crop-calculator__yield-note">
                    Expected yield advantage based on multi-year research conducted by The Mosaic Company.
                  </AppParagraph>
                </div>
              </div>

              <div class="crop-calculator__print-nutrient-information print">
                <AppHeading theme="black" spacing="xs" size="xl" class="crop-calculator__results-large-heading crop-calculator-nutrient-detail-heading crop-calculator-result-heading">
                  Nutrient Details
                </AppHeading>

                <div class="crop-calculator__nutrient-summary--container">
                  <table
                    class="crop-calculator__nutrient-summary--table"
                    align="center"
                    style="table-layout: fixed; width: 100%;"
                  >
                    <thead>
                      <tr>
                        <td align="center" colspan="50%">
                          <span class="crop-calculator__nutrient-summary--logo">
                            <AssetEmbed :key="performanceProductFormula.id" :asset="performanceProductFormula.logo" />
                          </span>

                          <span class="crop-calculator__nutrient-summary--cost">
                            <TextContent :content="costOfMicroessentialsValue" />
                            <small>per acre</small>
                          </span>
                        </td>
                        <td align="center" colspan="50%">
                          <span class="crop-calculator__nutrient-summary--blend-title">
                            <strong>Your Current</strong>
                            <br />
                            <strong>Blend</strong>
                          </span>

                          <span class="crop-calculator__nutrient-summary--cost">
                            <TextContent :content="currentBlendCostOfBlendValue" />
                            <small>per acre</small>
                          </span>
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td align="center" colspan="33%" class="element-result">
                          <TextContent :content="calculatorResults.microessentialsNitrogen" />
                          <TextContent :content="setWeightOption" />
                        </td>
                        <td align="center" colspan="33%" class="element-result">
                          N
                        </td>
                        <td align="center" colspan="34%" class="element-result">
                          <TextContent :content="calculatorResults.currentBlendTotalNitrogen" />
                          <TextContent :content="setWeightOption" />
                        </td>
                      </tr>
                      <tr>
                        <td align="center" colspan="33%" class="element-result">
                          <TextContent :content="calculatorResults.rateOfPhosphorusApplied" />
                          <TextContent :content="setWeightOption" />
                        </td>
                        <td align="center" colspan="33%" class="element-result">P₂O₅</td>
                        <td align="center" colspan="34%" class="element-result">
                          <TextContent :content="calculatorResults.rateOfPhosphorusApplied" />
                          <TextContent :content="setWeightOption" />
                        </td>
                      </tr>
                      <tr>
                        <td align="center" colspan="33%" class="element-result">
                          <TextContent :content="calculatorResults.microessentialsSulfateSulfur" />
                          <TextContent :content="setWeightOption" />
                        </td>
                        <td align="center" colspan="33%" class="element-result">SO₄²</td>
                        <td align="center" colspan="34%" class="element-result">
                          <TextContent :content="calculatorResults.currentBlendAmmoniumSulfateSo4Amount" />
                          <TextContent :content="setWeightOption" />
                        </td>
                      </tr>
                      <tr>
                        <td align="center" colspan="33%" class="element-result">
                          <TextContent :content="calculatorResults.microessentialsElementalSulfur" />
                          <TextContent :content="setWeightOption" />
                        </td>
                        <td align="center" colspan="33%" class="element-result">
                          S⁰
                        </td>
                        <td align="center" colspan="34%" class="element-result">
                          <TextContent :content="calculatorResults.currentBlendElementalSulfurAmount" />
                          <TextContent :content="setWeightOption" />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="crop-calculator-yellow-block">
                  <span>MicroEssentials S10 provides improved nutrient uptake and uniform nutrient distribution across every field.</span>
                </div>
              </div>

              <div class="print-hide">
                <AppHeading
                  v-if="performanceProductFormula.name !== productFormulaMS15 && selectedCropValue === cropCorn"
                  theme="black"
                  size="sm"
                  spacing="lg"
                  class="calc-section crop-calculator-heading"
                >
                  Let’s Talk About Crop Yield.
                </AppHeading>

                <AppParagraph
                  v-if="performanceProductFormula.name === productFormulaMS10 && selectedCropValue === cropCorn"
                  size="sm"
                  spacing="xl"
                  class="crop-calculator__space-x"
                >
                  Our long term research on corn has shown an average of
                  <span v-if="selectedFertilizer.name === 'DAP'">
                    <span class="text-bolder">5.4 bu/ac yield increase</span> for MicroEssentials S10 compared to DAP.
                  </span>
                  <span v-else>
                    <span class="text-bolder">4.7 bu/ac yield increase</span> for MicroEssentials S10 compared to MAP.
                  </span>
                </AppParagraph>

                <AppParagraph v-if="performanceProductFormula.name === productFormulaMS10 && selectedCropValue === cropCorn">
                  <AppHeading theme="black" size="sm" spacing="md" class="calc-section crop-calculator-heading">
                    Compare results for MicroEssentials vs:
                  </AppHeading>
                  <br />
                  <div class="crop-calculator__border-bottom-dotted large-padding">
                    <AppButton class="small-btn text-underline gray" @click="toggleDAPComparisonS10()">
                      DAP
                    </AppButton>
                    <AppButton class="small-btn text-underline gray" @click="toggleMAPComparisonS10()">
                      MAP
                    </AppButton>
                  </div>
                </AppParagraph>
              </div>
            </div>

            <ContentModal :isOpen="isDAPComparisonOpenS10" :close="toggleDAPComparisonS10" :headline="dapS10Title">
              <div class="crop-calculator__sample-blend--container print-hide">
                <AppHeading element="h3" spacing="xl" size="sm">
                  CORN RESPONSE TO MICROESSENTIALS S10 VS. DAP
                </AppHeading>
                <img :src="graphS10Dap" alt="graph MAP vs. MicroEssentials SZ" style="width: 75%;" />
                <AppParagraph size="sm" spacing="xl">
                  MicroEssentials S10 outperformed DAP by 3.5% or a 5.4 bu/acre increase in yield, on average, across all
                  locations.
                </AppParagraph>
                <AppHeading element="h3" spacing="xl" size="sm">
                  RESEARCH LOCATIONS
                </AppHeading>
                <img :src="graphSzMap" alt="Map of Research locations Canada and USA" style="width: 75%;" />
                <AppParagraph size="sm" spacing="xl" class="fst-italic">
                  The data is a summary of field studies conducted by third party, independent researchers in replicated
                  small plot research trials completed from 2004-2013. Averages for each treatment are based on a balanced
                  data set of 100 separate trials. Nutrient rates were equalized between treatments each year, with
                  P<sub>2</sub>O
                  <sub>5</sub>
                  rates ranging from 65-90 lbs.
                </AppParagraph>
                <AppParagraph spacing="xl" size="sm" class="text-center paragraph__bolder">
                  More yield data can be found at <br />
                  <a href="/microessentials/" class="txt-primary text-bolder">
                    cropnutrition.com/microessentials
                  </a>
                </AppParagraph>
              </div>
            </ContentModal>
            <ContentModal :isOpen="isMAPComparisonOpenS10" :close="toggleMAPComparisonS10" :headline="mapS10Title">
              <div class="crop-calculator__sample-blend--container print-hide">
                <AppHeading element="h3" spacing="xl" size="sm">
                  MicroEssentials S10® — Corn Study
                </AppHeading>
                <img :src="graphS10Map" alt="graph MAP vs. MicroEssentials SZ" style="width: 75%;" />
                <AppParagraph size="sm" spacing="sm" class="modal-p">
                  MicroEssentials S10® increased corn yield by 4.7 bu/ac over MAP across 47 trials in 17 states/provinces.
                </AppParagraph>
                <AppParagraph size="sm" spacing="xl">
                  Higher corn yields achieved by using MicroEssentials S10® demonstrates the value of uniform nutrient
                  distribution, increased nutrient uptake, and season-long S availability.
                </AppParagraph>
                <AppHeading element="h3" spacing="xl" size="sm">
                  RESEARCH LOCATIONS
                </AppHeading>
                <img
                  :src="graphS10LocationMap"
                  alt="Map of Research locations Canada and USA"
                  style="width: 75%;"
                />
                <AppParagraph spacing="xl" size="sm" class="modal-p">
                  The data is a summary of field studies conducted by third party, independent researchers in 47 trials
                  across the US and Canada - IA, IL, IN, KS, KY, MB, MI, MN, MO, MS, ND, NE, OH, ON, SC, SD, WI completed
                  from 2017-2018. Experimental Design: Small-plot RCBD with 4 replications. 80 lbs P2O5/ac applied as MAP
                  (11-52-0) or MicroEssentials S10 (12-40-0-10S), 20 lbs S/ac from the MicroEssentials S10 treatment, N rate
                  was balanced across all treatments, K rate as required by soil test.
                </AppParagraph>
                <AppParagraph spacing="xl" size="sm" class="text-center paragraph__bolder">
                  More yield data can be found at <br />
                  <a href="/microessentials/" class="txt-primary text-bolder">
                    cropnutrition.com/microessentials
                  </a>
                </AppParagraph>
              </div>
            </ContentModal>

            <div class="print-hide">
              <AppButton class="crop-calculator__view-btn print-hide crop-calculator__calc-btn" @click="openPersonalization()">
                Generate Report
              </AppButton>

              <ContentModal :isOpen="isPersonalizationModalOpen" :close="closePersonalization" :headline="'Personalize'">
                <div class="personalized-modal">
                  <TextContent :content="'Prepared For'" />
                  <input v-model="form.personalization.preparedFor" />
                  <TextContent :content="'Prepared By'" />
                  <input v-model="form.personalization.preparedBy" />
                  <TextContent :content="'Company'" />
                  <input v-model="form.personalization.company" />
                  <TextContent :content="'Phone Number'" />
                  <input v-model="form.personalization.phoneNumber" />
                  <!-- <AppButton class="crop-calculator__calculate-button" @click="generatePdf()">Generate Report</AppButton> -->
                  <PrintPDF :fileName="'S10 Yield Calculation Results'" :generateCallback="closePersonalization" />
                </div>
              </ContentModal>
                
             
              <!-- This button and the sending of emails has been disabled until Phase 2
              <AppButton class="crop-calculator__send-btn" @click="openModal()">
                Send report as an email
              </AppButton> -->
            </div>

            <ContentModal :isOpen="isNutrientSummaryOpen" :close="closeNutrientSummary" :headline="nutrientSummaryTitle">
              <div class="crop-calculator__nutrient-summary--container print-hide">
                <table
                  class="crop-calculator__nutrient-summary--table"
                  align="center"
                  style="table-layout: fixed; width: 100%;"
                >
                  <thead>
                    <tr>
                      <td align="center" colspan="50%">
                        <span class="crop-calculator__nutrient-summary--logo">
                          <AssetEmbed :key="performanceProductFormula.id" :asset="performanceProductFormula.logo" />
                        </span>

                        <span class="crop-calculator__nutrient-summary--cost">
                          <TextContent :content="costOfMicroessentialsValue" />
                          <small>per acre</small>
                        </span>
                      </td>
                      <td align="center" colspan="50%">
                        <span class="crop-calculator__nutrient-summary--blend-title">
                          <strong>Your Current</strong>
                          <br />
                          <strong>Blend</strong>
                        </span>
                        
                        <span class="crop-calculator__nutrient-summary--cost">
                          <TextContent :content="currentBlendCostOfBlendValue" />
                          <small>per acre</small>
                        </span>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td align="center" colspan="33%" class="element-result">
                        <TextContent :content="calculatorResults.microessentialsNitrogen" />
                        <TextContent :content="setWeightOption" />
                      </td>
                      <td align="center" colspan="33%" class="element-result">
                        N
                      </td>
                      <td align="center" colspan="34%" class="element-result">
                        <TextContent :content="calculatorResults.currentBlendTotalNitrogen" />
                        <TextContent :content="setWeightOption" />
                      </td>
                    </tr>
                    <tr>
                      <td align="center" colspan="33%" class="element-result">
                        <TextContent :content="calculatorResults.rateOfPhosphorusApplied" />
                        <TextContent :content="setWeightOption" />
                      </td>
                      <td align="center" colspan="33%" class="element-result">P₂O₅</td>
                      <td align="center" colspan="34%" class="element-result">
                        <TextContent :content="calculatorResults.rateOfPhosphorusApplied" />
                        <TextContent :content="setWeightOption" />
                      </td>
                    </tr>
                    <tr>
                      <td align="center" colspan="33%" class="element-result">
                        <TextContent :content="calculatorResults.microessentialsSulfateSulfur" />
                        <TextContent :content="setWeightOption" />
                      </td>
                      <td align="center" colspan="33%" class="element-result">SO₄²</td>
                      <td align="center" colspan="34%" class="element-result">
                        <TextContent :content="calculatorResults.currentBlendAmmoniumSulfateSo4Amount" />
                        <TextContent :content="setWeightOption" />
                      </td>
                    </tr>
                    <tr>
                      <td align="center" colspan="33%" class="element-result">
                        <TextContent :content="calculatorResults.microessentialsElementalSulfur" />
                        <TextContent :content="setWeightOption" />
                      </td>
                      <td align="center" colspan="33%" class="element-result">
                        S⁰
                      </td>
                      <td align="center" colspan="34%" class="element-result">
                        <TextContent :content="calculatorResults.currentBlendElementalSulfurAmount" />
                        <TextContent :content="setWeightOption" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ContentModal>
          </div>
          <!--Print Detail Right Ends here-->
        </div>
      </div>
    </div>
    <!--End Right Side-->
  </div>
</template>

<script>
import _ from 'lodash';

import AppButton from '@/components/shared/AppButton';
import AppContainer from '@/components/shared/AppContainer';
import AppFlexRow from '@/components/shared/AppFlexRow';
import AppFlexCol from '@/components/shared/AppFlexCol';
import AppHeading from '@/components/shared/AppHeading';
import AppSubheading from '@/components/shared/AppSubheading';
import AppParagraph from '@/components/shared/AppParagraph';
import AssetEmbed from '@/components/shared/AssetEmbed';
import CircularProgress from '@/components/shared/CircularProgress';
import CropList from '@/components/shared/CropList';
import ContentModal from '@/components/shared/ContentModal';
import CalculatorResults from '@/utils/calculator-results';
import LocalRetailPriceInput from '@/components/LocalRetailPriceInput.vue';
import PrintDetail from '@/components/PrintDetail';
import PrintPersonalization from '@/components/PrintPersonalization.vue';
import PrintPDF from '@/components/PrintPDF.vue';
import TextContent from '@/components/shared/TextContent';
// import YieldEmailModal from '@/components/YieldEmailModal'; // phase two
import IconSZ from '@/components/icons/IconSZ';
import IconS10 from '@/components/icons/IconS10';
import IconS15 from '@/components/icons/IconS15';
import VueSlider from 'vue-3-slider-component'
import { dispatchCalculateEvent } from '@/sfmc/campaign-utilities';

import exportPdf from '@/utils/print-pdf';
import TextInput from '@/components/shared/TextInput';

import { format } from 'date-fns';

export default {
  components: {
    AppButton,
    AppContainer,
    AppFlexRow,
    AppFlexCol,
    AppHeading,
    AppSubheading,
    AppParagraph,
    AssetEmbed,
    CircularProgress,
    ContentModal,
    CropList,
    LocalRetailPriceInput,
    PrintDetail,
    PrintPersonalization,
    PrintPDF,
    TextContent,
    IconSZ,
    IconS10,
    IconS15,
    VueSlider,
    TextInput
  },
  props: {
    performanceProductFormula: {
      type: Object,
      required: true
    },
    previousCalculator: {
      type: Object,
      required: false,
      default: undefined
    }
  },
  inject: ['getPdfEl'],
  data() {
    const fertilizer = this.performanceProductFormula.fertilizers[0];

    let cropData;

    if (this.previousCalculator) {
      cropData = {
        selectedCropId: this.previousCalculator.selectedCropId,
        selectedFertilizerId: this.previousCalculator.selectedFertilizerId,
        selectedPrice: this.previousCalculator.selectedPrice,
        selectedYield: this.previousCalculator.selectedYield
      };
    } else {
      cropData = {
        selectedCropId: fertilizer.crop.id,
        selectedFertilizerId: fertilizer.id,
        selectedPrice: fertilizer.crop.defaultPrice,
        selectedYield: fertilizer.defaultYield
      };
    }

    return {
      graphSzMap: window.host_domain + '/images/graph_sz_map.jpeg',
      graphS10Dap: window.host_domain + '/images/graph_s10_dap.png',
      graphS10Map: window.host_domain + '/images/graph_s10_map.png',
      graphS10LocationMap: window.host_domain + '/images/graph_s10_location_map.png',
      isModalOpen: false,
      append: '$$#',
      cropCorn: 'Corn',
      isDAPComparisonOpen: false,
      isDAPComparisonOpenS10: false,
      isMAPComparisonOpenS10: false,
      isMAPComparisonOpen: false,
      isNutrientSummaryOpen: false,
      isNitrogenSummaryOpen: false,
      isSampleBlendOpen: false,
      isPersonalizationModalOpen: false,
      dapTitle: 'MicroEssentials SZ vs DAP',
      dapS10Title: 'MicroEssentials S10 vs DAP',
      mapS10Title: 'MicroEssentials S10 vs MAP',
      mapTitle: 'MicroEssentials SZ vs MAP',
      nitrogenTitle: 'Balance for Nitrogen',
      nutrientSummaryTitle: 'Nutrient Summary',
      productFormulaMSZ: 'MicroEssentials SZ®',
      productFormulaMS10: 'MicroEssentials S10®',
      productFormulaMS15: 'MicroEssentials S15®',
      sampleBlendTitle: 'Sample Blends',
      logo: this.performanceProductFormula.logo.url,
      name: this.performanceProductFormula.name,
      dataStyle: {
        position: 'relative',
        transition: '1s',
        transitionEasingFunction: 'ease-in'
      },
      year: format(new Date(), 'y'),
      windowWidth: 0,
      currentPage: 'inputs',
      formYield: 0,
      initialYield: cropData.selectedYield,
      phosAppliedValue: '',
      loaded: 0,
      ...cropData,
      metricUnit: 'metric tonne',
      originalTop: 0,
      minimumYield: 1,
      maximumYield: 10,
      conversions: {
        shortTonToPounds: 0.0005,
        shortTonToKilograms: 0.001102,
        metricTonToPounds: 0.000454,
        metricTonToKilograms: 0.001
      },

      rules: {
        ammoniumSulfate: {
          nitrogen: 21,
          sulfate_sulfur: 24
        },
        dap: {
          phosphate: 46,
          nitrogen: 18
        },
        map: {
          phosphate: 52,
          nitrogen: 11
        },
        urea: {
          name: 'Urea',
          nitrogen: 46
        }
      },
      blends: {
        ammoniumSulfateBlend: {
          percent_100_ammonium_sulfate: {
            name: '100% Ammonium Sulfate (AMS)',
            ammonium_sulfate: 100,
            nitrogen: 21,
            sulfate_sulfur: 24,
            keywords: ['Ammonium Sulfate']
          },
          percent_50_elemental_sulfur: {
            name: '50% AMS - 50% Elemental Sulfur',
            ammonium_sulfate: 50,
            elemental_sulfur: 90,
            nitrogen: 21,
            sulfate_sulfur: 24,
            keywords: ['AMS', 'Elemental Sulfur']
          },
          percent_100_elemental_sulfur: {
            name: '100% Elemental Sulfur (0-0-0-90S)',
            nitrogen: 0,
            sulfate_sulfur: 90,
            ammonium_sulfate: 0,
            keywords: ['Elemental Sulfur (0-0-0-90S)']
          }
        }
      },
      unitsOfMeasure: [
        { name: 'short ton', id: 1 },
        { name: 'metric tonne', id: 2 }
      ],
      weightOptions: ['bu/ac', 'kg/ha', 'lb/ac'],
      weightOptionInputMapping: {
        'bu/acre': 'lb/acre',
        'kg/hectare': 'kg/hectare',
        'lb/acre': 'lb/acre'
      },
      form: {
        weightOption: 'bu/ac',
        emailWeight: 'bu/ac',
        selectedUnit: 'lbs/ac',
        selectedArea: '/ac',
        emailCropUnit: '/bu',
        retailPrice: null,
        formulaName: 'MicroEssentials S10®',
        yield: cropData.selectedYield,
        balanceNitrogen: false,
        rateOfPhosphorusApplied: 0,
        sulfurBlend: 'percent_100_ammonium_sulfate',
        unitOfMeasure: 'short ton',
        percentOfAmmoniumSulfateinSulfurBlend: 100,
        localRetailPrice: {
          ammoniumSulfate: null,
          currentBlend: null,
          elementalSulfur: null,
          cropnutrition: null,
          urea: null
        },
        personalization: {
          company: null,
          preparedFor: null,
          preparedBy: null,
          phoneNumber: null
        }
      }
    };
  },
  computed: {
    calculatorResults() {
      const calc_result = new CalculatorResults({
        costOfMicroessentials: this.costOfMicroessentials,
        currentBlendCostOfBlend: this.currentBlendCostOfBlend,
        currentBlendElementalSulfurAmount: this.currentBlendElementalSulfurAmount,
        currentBlendAmmoniumSulfateSo4Amount: this.currentBlendAmmoniumSulfateSo4Amount,
        currentBlendTotalNitrogen: this.currentBlendTotalNitrogen,
        currentBlendAmountOfZinc: 0,
        form: this.form,
        formula: this.performanceProductFormula.slug,
        microessentialsSulfateSulfur: this.microessentialsSulfateSulfur,
        microessentialsElementalSulfur: this.microessentialsElementalSulfur,
        microessentialsNitrogen: this.microessentialsTotalNitrogen,
        microessentialsZinc: 0,
        rateOfPhosphorusApplied: Number(this.form.rateOfPhosphorusApplied).toFixed(2),
        roiIncrease: this.microessentialsIncrementalProfit,
        yieldAdvantage: Math.round(this.formYield * 10) / 10,
        crop: this.selectedCropValue,
        cropPrice: this.form.retailPrice,
        currentBlend: this.selectedFertilizer.name,
        phosphorusRate: this.form.rateOfPhosphorusApplied,
        sulfurBlend: this.form.sulfurBlend && this.blends.ammoniumSulfateBlend[this.form.sulfurBlend].name,
        additionalRevenue: this.microessentialsGrossIncrementalRevenue,
        additionalInvestment: this.additionalInvestment,
        preparedFor: this.form.personalization.preparedFor,
        preparedBy: this.form.personalization.preparedBy,
        company: this.form.personalization.company,
        phoneNumber: this.form.personalization.phoneNumber
      });
      return calc_result;
    },
    conversionRate() {
      if (this.form.weightOption.includes('kg')) return 1.12085;
      return 1;
    },
    setWeightOption() {
      if (this.form.weightOption === 'bu/ac' || this.form.weightOption === 'lb/ac') {
        return ' lbs/ac';
      }
      return ' kg/ha';
    },
    chartValue() {
      let value = this.microessentialsIncrementalProfit;
      if (value <= 0) {
        return 0;
      }

      return value < 100 ? value * 1.15 : value;
    },
    crops() {
      const crops = this.performanceProductFormula.fertilizers.map(f => f.crop);

      return _.uniqBy(crops, 'id');
    },
    cropNames() {
      return this.crops.map(c => c.crop);
    },
    fertilizers() {
      return this.performanceProductFormula.fertilizers.filter(f => this.selectedCropId === f.crop.id);
    },
    selectedCrop() {
      return this.crops.find(c => c.id === this.selectedCropId);
    },
    selectedCropValue: {
      get() {
        return this.selectedCrop.crop;
      },
      set(crop) {
        this.selectedCropId = this.crops.find(c => c.crop === crop).id;
      }
    },
    selectedFertilizer() {
      return this.performanceProductFormula.fertilizers.find(f => f.id === this.selectedFertilizerId);
    },
    amountOfMicroEssentials() {
      // amount of microessentials is calculated by the current amount of phosphorus applied and the
      // the rate of phosphorus in the microessentials mix so that phosphorus mix remains constant

      if (!this.form.rateOfPhosphorusApplied) return 0;
      return this.form.rateOfPhosphorusApplied / this.performanceProductFormula.phosphorus;
    },
    selectedAmmoniumSulfateBlend() {
      return this.blends.ammoniumSulfateBlend[this.form.sulfurBlend];
    },
    ammoniumSulfateBlends() {
      return Object.keys(this.blends.ammoniumSulfateBlend).map(k => ({
        value: k,
        name: this.blends.ammoniumSulfateBlend[k].name
      }));
    },
    // Blend amount calculations
    currentBlendAmmoniumSulfateSo4Amount() {
      if (this.form.percentOfAmmoniumSulfateinSulfurBlend === 0) {
        return 0;
      }
      if (this.form.percentOfAmmoniumSulfateinSulfurBlend === 50) {
        return this.microessentialsSulfateSulfur;
      }
      if (this.form.percentOfAmmoniumSulfateinSulfurBlend === 100) {
        return this.currentBlendNetSulfur;
      }

      return 0;
    },
    currentBlendNetSulfur() {
      return this.microessentialsCombinedSulfur;
    },
    microessentialsSulfateSulfur() {
      return this.amountOfMicroEssentials * this.performanceProductFormula.sulfate;
    },
    microessentialsCombinedSulfur() {
      return this.microessentialsElementalSulfur + this.microessentialsSulfateSulfur;
    },
    microessentialsElementalSulfur() {
      return this.amountOfMicroEssentials * this.performanceProductFormula.sulfur;
    },
    currentBlendNitrogenInSources() {
      return this.currentBlendAmmoniumSulfateNitrogenAmount + this.currentBlendNitrogenAmount;
    },
    currentBlendAmmoniumSulfateNitrogenAmount() {
      if (this.form.percentOfAmmoniumSulfateinSulfurBlend === 0) return 0;

      return (
        this.currentBlendTotalAmmoniumSulfate * (this.blends.ammoniumSulfateBlend[this.form.sulfurBlend].nitrogen / 100)
      );
    },
    currentBlendNitrogenAmount() {
      if (this.form.balanceNitrogen === true) {
        let getRate = 0;
        let retVal = 0;

        if (this.selectedFertilizer.name === 'MAP') {
          getRate = this.form.rateOfPhosphorusApplied / (this.rules.map.phosphate / 100);
          retVal = getRate * (this.rules.map.nitrogen / 100);
        } else {
          getRate = this.form.rateOfPhosphorusApplied / (this.rules.dap.phosphate / 100);
          retVal = getRate * (this.rules.dap.nitrogen / 100);
        }

        return retVal;
      }

      if (_.isEmpty(this.selectedFertilizer)) return 0;
      return this.amountOfBlend * (this.selectedFertilizer.nitrogen / 100);
    },
    currentBlendTotalNitrogen() {
      if (!this.form.balanceNitrogen) return this.currentBlendNitrogenInSources;
      if (this.currentBlendNitrogenInSources >= this.microessentialsNitrogen) return this.currentBlendNitrogenInSources;
      return this.microessentialsNitrogen;
    },
    currentBlendPercentOfSulfurinSulfurBlend() {
      return 100 - Number(this.form.percentOfAmmoniumSulfateinSulfurBlend);
    },
    currentBlendElementalSulfurAmount() {
      if (this.currentBlendPercentOfSulfurinSulfurBlend === 0) {
        return 0;
      }
      if (this.currentBlendPercentOfSulfurinSulfurBlend === 50) {
        return this.microessentialsElementalSulfur;
      }
      if (this.currentBlendPercentOfSulfurinSulfurBlend === 100) {
        return this.currentBlendNetSulfur;
      }
      return 0;
    },
    currentBlendTiger90Amount() {
      return (
        this.currentBlendElementalSulfurAmount /
        (this.blends.ammoniumSulfateBlend.percent_100_elemental_sulfur.sulfate_sulfur / 100)
      );
    },
    currentBlendTotalAmmoniumSulfate() {
      return this.currentBlendAmmoniumSulfateSo4Amount / (this.rules.ammoniumSulfate.sulfate_sulfur / 100);
    },
    currentBlendCostOfBlend() {
      if (_.isEmpty(this.selectedFertilizer)) return 0;
      if (
        this.form.sulfurBlend === 'percent_100_elemental_sulfur' &&
        this.form.balanceNitrogen === true &&
        this.selectedFertilizer.name === 'MAP'
      ) {
        return (
          this.form.localRetailPrice.currentBlend * this.tonsToWeight * this.amountOfBlend +
          this.currentBlendCostOfAmmoniumSulfate +
          this.currentBlendCostOfElementalSulfur +
          this.currentBlendCostOfUrea
        );
      }
      return (
        this.form.localRetailPrice.currentBlend * this.tonsToWeight * this.amountOfBlend +
        this.currentBlendCostOfAmmoniumSulfate +
        this.currentBlendCostOfElementalSulfur
      );
    },
    currentBlendCostOfAmmoniumSulfate() {
      if (Number(this.form.localRetailPrice.ammoniumSulfate) === 0) return 0;
      if (this.form.sulfurBlend === 'percent_100_elemental_sulfur') return 0;
      return Number(this.form.localRetailPrice.ammoniumSulfate) * this.tonsToWeight * this.currentBlendTotalAmmoniumSulfate;
    },
    currentBlendCostOfElementalSulfur() {
      return this.currentBlendTiger90Amount * (Number(this.form.localRetailPrice.elementalSulfur) * this.tonsToWeight);
    },
    additionalInvestment() {
      return this.costOfMicroessentials - this.currentBlendCostOfBlend;
    },
    additionalNitrogenAddedToMicroessentials() {
      if (this.form.balanceNitrogen !== true) return 0;
      if (this.currentBlendNitrogenInSources <= this.microessentialsNitrogen) return 0;
      return this.currentBlendNitrogenInSources - this.microessentialsNitrogen;
    },

    microessentialsAmountOfUrea() {
      return this.additionalNitrogenAddedToMicroessentials / (this.rules.urea.nitrogen / 100);
    },

    microessentialsCostOfUrea() {
      return this.microessentialsAmountOfUrea * (Number(this.form.localRetailPrice.urea) * this.tonsToWeight);
    },

    currentBlendAmountOfUrea() {
      if (this.form.balanceNitrogen !== true) return 0;
      if (this.currentBlendNitrogenInSources >= this.microessentialsNitrogen) return 0;

      return (this.microessentialsNitrogen - this.currentBlendNitrogenInSources) / (this.rules.urea.nitrogen / 100);
    },

    currentBlendCostOfUrea() {
      return this.currentBlendAmountOfUrea * (Number(this.form.localRetailPrice.urea) * this.tonsToWeight);
    },

    microessentialsTotalNitrogen() {
      return this.microessentialsNitrogen + this.additionalNitrogenAddedToMicroessentials;
    },
    microessentialsNitrogen() {
      return this.amountOfMicroEssentials * this.performanceProductFormula.nitrogen;
    },
    microessentialsGrossIncrementalRevenue() {
      if (Number(this.formYield) === 0 || Number(this.form.retailPrice) === 0) return 0;
      return Number(this.formYield) * Number(this.form.retailPrice);
    },
    costOfMicroessentials() {
      if (Number(this.form.localRetailPrice.cropnutrition) === 0) return 0;

      return (
        this.amountOfMicroEssentials * (this.form.localRetailPrice.cropnutrition * this.tonsToWeight) +
        this.microessentialsCostOfUrea
      );
    },
    microessentialsIncrementalProfit() {
      return this.microessentialsGrossIncrementalRevenue - this.additionalInvestment;
    },
    shortTonsToWeight() {
      if (this.form.weightOption.includes('kg')) {
        return this.conversions.shortTonToKilograms;
      }

      return this.conversions.shortTonToPounds;
    },
    metricTonsToWeight() {
      if (this.form.weightOption.includes('kg')) {
        return this.conversions.metricTonToKilograms;
      }

      return this.conversions.metricTonToPounds;
    },
    tonsToWeight() {
      return this.form.unitOfMeasure === this.metricUnit ? this.metricTonsToWeight : this.shortTonsToWeight;
    },
    amountOfBlend() {
      if (!this.form.rateOfPhosphorusApplied || _.isEmpty(this.selectedFertilizer)) return 0;

      return this.form.rateOfPhosphorusApplied / (this.selectedFertilizer.phosphorus / 100);
    },
    currentBlendNitrogenInZincSource() {
      if (!this.form.rateOfZincApplied || _.isEmpty(this.form.zincBlend)) return 0;
      return (this.currentBlendZincAmount * Number(this.blends.zincBlend[this.form.zincBlend].sulfate_sulfur)) / 100;
    },
    calcHeightInputs() {
      const add = this.form.balanceNitrogen === true ? 190 : 70;
      const add1 = this.selectedAmmoniumSulfateBlend.name === '50% AMS - 50% Elemental Sulfur' ? 190 : 70;

      if (this.windowWidth < 330) {
        return 3490 + add + add1;
      } else if (this.windowWidth < 400) {
        return 3200 + add + add1;
      } else if (this.windowWidth < 460) {
        return 3050 + add + add1;
      } else if (this.windowWidth < 604) {
        return 2800 + add + add1;
      } else if (this.windowWidth < 720) {
        return 2750 + add + add1;
      } else if (this.windowWidth < 868) {
        return 2850 + add + add1;
      } else {
        return 2800 + add + add1;
      }
    },
    calcHeightRoi() {
      const add = this.selectedCropValue === 'Corn' ? 0 : -312;
      if (this.windowWidth < 400) {
        return 1730 + add;
      } else if (this.windowWidth < 720) {
        return 1530 + add;
      } else if (this.windowWidth < 960) {
        return 1550 + add;
      } else {
        return 1920 + add;
      }
    },
    calcStyle() {
      const height = this.currentPage === 'inputs' ? this.calcHeightInputs : this.calcHeightRoi;
      if (this.loaded === 1) {
        return 'opacity: 1; min-height: ' + height + 'px';
      }
      return 'min-height: ' + height + 'px';
    },
    inputStyle() {
      return this.currentPage === 'inputs' ? 'left: 0' : 'left: -' + this.windowWidth + 'px';
    },
    roiStyle() {
      const setting = this.currentPage === 'inputs' ? 'left: ' + this.windowWidth + 'px' : 'left: 1px';
      if (this.loaded === 1) {
        return 'display: block;' + setting;
      }
      return setting;

    },
    yieldChange() {
      return this.form.yield;
    },
    unitChange() {
      return this.form.weightOption;
    },
    cropPoundConvert() {
      return this.selectedCropValue === 'Corn' ? 56 : 60;
    },
    cropKilogramsConvert() {
      return this.selectedCropValue === 'Corn' ? 62.77 : 67.25;
    },
    roiIncrease() {
      return `${parseFloat(this.microessentialsIncrementalProfit).toFixed(2)}`;
    },
    additionalRevenueValue() {
      return `$${parseFloat(this.microessentialsGrossIncrementalRevenue).toFixed(2)}`;
    },
    additionalInvestmentValue() {
      return `$${parseFloat(this.additionalInvestment).toFixed(2)}`;
    },
    costOfMicroessentialsValue() {
      return `$${parseFloat(this.costOfMicroessentials).toFixed(2)}`;
    },
    currentBlendCostOfBlendValue() {
      return `$${parseFloat(this.currentBlendCostOfBlend).toFixed(2)}`;
    }
  },

  watch: {
    selectedFertilizer(selectedFertilizer) {
      this.selectedYield = selectedFertilizer.defaultYield;
      this.form.yield = selectedFertilizer.defaultYield;
      this.initialYield = selectedFertilizer.defaultYield;
      this.convertUnits();
    },
    selectedCrop(selectedCrop) {
      this.selectedPrice = selectedCrop.defaultPrice;
    },
    fertilizers(fertilizers) {
      this.selectedFertilizerId = fertilizers[0].id;
    },
    selectedAmmoniumSulfateBlend(sulfurBlend) {
      this.form.percentOfAmmoniumSulfateinSulfurBlend = sulfurBlend.ammonium_sulfate;
    },
    unitChange() {
      this.convertUnits();
      this.convertMaxYield();
      this.form.emailCropUnit = '/' + this.form.weightOption.split('/')[0];
    },
    setWeightOption(setWeightOption) {
      this.form.selectedUnit = setWeightOption;
      this.form.selectedArea = '/' + setWeightOption.split('/')[1];
    },
    'form.weightOption'() {
      this.form.emailWeight = this.form.weightOption;
    },
    phosAppliedValue(phosAppliedValue) {
      this.form.rateOfPhosphorusApplied = phosAppliedValue;
    }
  },
  mounted() {
    this.root = document.documentElement;
    this.root.style.setProperty('--color-primary', '#baa11f');
    this.formYield = this.form.yield;
    window.scrollTo(0, 0);
    this.windowWidth = window.innerWidth;
    setTimeout(() => {
      this.loaded = 1;
      this.$refs['calc'].style.opacity = 1;
    }, 1000);
    window.addEventListener('resize', () => {
      this.windowWidth = window.innerWidth;
    });
  },
  methods: {
    isFertilizerSelected(id) {
      return id === this.selectedFertilizerId;
    },
    closeModal() {
      this.isModalOpen = false;
    },
    openModal() {
      this.isModalOpen = true;
    },
    closeNutrientSummary() {
      this.isNutrientSummaryOpen = false;
    },
    openNutrientSummary() {
      this.isNutrientSummaryOpen = true;
    },
    closeSampleBlend() {
      this.isSampleBlendOpen = false;
    },
    openSampleBlend() {
      this.isSampleBlendOpen = true;
    },
    closePersonalization() {
      this.isPersonalizationModalOpen = false;
    },
    generatePdf() {
      this.isPersonalizationModalOpen = false;
      console.log(this.form);
      exportPdf(this.pdfEl, 'S10 Yield Calculation Results');
    },
    openPersonalization() {
      this.isPersonalizationModalOpen = true;
    },
    toggleMAPComparison() {
      this.isMAPComparisonOpen = !this.isMAPComparisonOpen;
    },
    toggleDAPComparison() {
      this.isDAPComparisonOpen = !this.isDAPComparisonOpen;
    },
    toggleDAPComparisonS10() {
      this.isDAPComparisonOpenS10 = !this.isDAPComparisonOpenS10;
    },
    toggleMAPComparisonS10() {
      this.isMAPComparisonOpenS10 = !this.isMAPComparisonOpenS10;
    },
    toggleNitrogenInformation() {
      this.isNitrogenSummaryOpen = !this.isNitrogenSummaryOpen;
    },
    showResults() {
      dispatchCalculateEvent();
      this.currentPage = 'roi';
      window.scrollTo(0, 0);
    },
    showInputs() {
      this.currentPage = 'inputs';
    },
    resetYield() {
      this.convertUnits(this.initialYield);
      this.form.yield = this.initialYield;
    },
    blurHandler() {
      const yld = this.formYield;
      let num;
      if (this.form.weightOption === 'lb/ac') {
        num = yld / this.cropPoundConvert;
      } else if (this.form.weightOption === 'kg/ha') {
        num = yld / this.cropKilogramsConvert;
      } else {
        num = yld;
      }
      this.formYield = this.formYield.toFixed(1);
      this.form.yield = num;
    },
    convertUnits(par) {
      const yld = typeof par !== 'undefined' ? par : this.yieldChange;
      let num;
      if (this.form.weightOption === 'lb/ac') {
        num = yld * this.cropPoundConvert;
      } else if (this.form.weightOption === 'kg/ha') {
        num = yld * this.cropKilogramsConvert;
      } else {
        num = yld;
      }
      this.formYield = num.toFixed(1);
    },
    convertMaxYield() {
      let max = 10;
      if (this.form.weightOption === 'lb/ac' || this.form.weightOption === 'kg/ha') {
        max = 500;
      }
      this.maximumYield = max;
    },
    handlePriceBlur() {
      this.form.retailPrice = parseFloat(this.form.retailPrice).toFixed(2);
    },
    handleSulfateBlendBlur() {
      this.phosAppliedValue = parseFloat(this.phosAppliedValue).toFixed(2);
    },
    getTodaysDate() {
      const date = new Date();
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    },
    currentBlendPDF() {
      if (this.selectedFertilizer.name === 'MAP') {
        return 'MAP 11-52-0';
      } else {
        return 'DAP 18-46-0';
      }
    }
  }
};
</script>

<style lang="scss" scoped>

.crop-calculator {
  opacity: 0;
  overflow: hidden;
  position: relative;
  transition: min-height 0.3s ease;

  .tester-tag {
    color: color('gray');
  }

  .calc-logo {
    margin-bottom: 0.5rem;
    width: 50%;

    @include breakpoint('md') {
      margin-bottom: 1.4rem;
      width: 168px;
    }
  }

  .black {
    color: #000;
  }
  .gray {
    color: color('gray');
  }

  .text-underline {
    text-decoration: underline;
  }
  .large-heading {
    padding: 1.2rem 0 1.7rem;

    @include breakpoint('md') {
      font-size: 2.75rem;
      padding-top: 2.2rem;
    }
  }

  .border-bottom-st-sf {
    border-bottom: 1px solid shade('light', 10%);
  }

  .no-padding {
    padding-bottom: 0.3rem;
  }

  .small-padding {
    padding-bottom: 1rem;
  }
  
  .small-margin {
    margin-bottom: 1.5rem;
  }

  .medium-margin {
    margin-bottom: 2.5rem;
  }

  .large-padding {
    padding-bottom: 3rem;
  }

  .small-btn {
    font-size: 0.8125rem;
    &:first-child {
      margin-right: 1.5rem;
    }
  }

  &__header-h1 {
    font-size: 2.65rem;
    margin-bottom: 2.45rem;

    @include breakpoint('md') {
      font-size: 5.25rem;
      margin-bottom: 2.55rem;
    }
  }

  &__subheading {
    color: color('gray');
    font-size: 1.25rem;
  }

  &__yield-note {
    &.paragraph {
      margin-bottom: 3rem;
    }
  }

  &__reset-btn {
    font-size: 0.8125rem;
    margin-top: 1rem;
    padding-bottom: 1.5rem;
    text-decoration: underline;

    &.button--primary {
      color: color('gray');
    }

    :deep(svg) {
      display: none;
    }

    :deep(.button__icon) {
      display: none;
    }
  }

  &__view-btn {
    display: flex;
    justify-content: center;
    margin: 1rem 0 3rem;
    text-decoration: underline;
    width: 100%;
    max-width: 250px;
    padding: 1.1rem 1.3125rem 1rem;
    text-decoration: none;

    &.button--primary {
      color: color('gray');
    }
  }

  &__return-btn {
    font-size: 0.8125rem;
    margin-bottom: spacing('lg');

    &.button--primary {
      color: color('gray');
    }

    :deep(svg) {
      fill: color('microessentials-yellow');
      transform: rotate(180deg);
    }
  }

  &__options,
  &__units {
    margin: 0 0 3rem;

    :deep(.crop-list) {
      justify-content: space-around;
      margin: 0 auto;
      max-width: 436px;

      @include breakpoint('md') {
        justify-content: center;
      }

      .crop-button {
        margin: 0.3125rem 0.5rem 1.5rem;
        width: 5rem;

        @include breakpoint('md') {
          margin: 0.3125rem 0.35rem 0.5rem;
        }
      }

      .crop-icon {
        height: 5rem;
        width: 5rem;

        svg {
          height: 44px;
          width: 44px;
        }

        &--border {
          border: 1px solid color('gray');
        }

        &--primary {
          background-color: transparent;
          border: 2px solid color('microessentials-yellow');

          svg {
            fill: color('microessentials-yellow');
          }
        }

        @include breakpoint('md') {
          height: 4rem;
          width: 4rem;

          svg {
            height: 29px;
            width: 29px;
          }
        }
      }

      &:hover::after {
        background-color: transparent;
      }
    }
  }

  &__units {
    margin-bottom: 3.6rem;
  }

  &__no-border {
    border-bottom: 1px solid shade('light', 10%);
    border-top: 1px solid shade('light', 10%);
    margin: 2.25rem 0;
    padding: 2.25rem 0;
  }

  &__calculator {
    padding: 0 spacing('lg');
    position: absolute;
    transition: left 0.4s ease;
    width: 100%;
  }

  &__roi {
    display: none;
    left: 2200px;
    position: absolute;
    text-align: center;
    transition: left 0.4s ease;
    width: 100%;

    .flex-row {
      margin-bottom: 3.6rem;
    }
  }

  &__additional-totals {
    @include breakpoint('md') {
      h4 {
        font-size: 2.125rem;

        small {
          font-size: 1.25rem;
        }
      }
    }
  }

  &__input {
    color: color('black');
    margin-bottom: spacing('xl');
    padding-bottom: 3.5rem;
    position: relative;

    :deep(.dropdown) {
      position: absolute;
      right: 0;
    }
  }

  &__border-bottom {
    border-bottom: 1px solid shade('light', 10%);
  }

  &__border-bottom-dotted {
    border-bottom: 1px dashed shade('light', 10%);
  }

  &__default-price-note {
    color: color('gray');
    margin-top: spacing('lg');
  }

  &__space {
    margin-bottom: spacing('xl', 2);
  }

  &__space-x {
    color: color('gray');
    font-size: 0.8125rem;
    max-width: 455px;

    &.paragraph {
      margin-left: auto;
      margin-right: auto;
    }
  }

  &__numeric {
    font-family: $articulat-cf;
    font-weight: 700;
    font-size: 2.5rem;
    margin-bottom: spacing('sm');

    small {
      color: color('black');
      font-size: 20px;
      position: relative;
      text-transform: uppercase;

      &.smaller {
        font-size: 0.9375rem;

        @include breakpoint('md') {
          font-size: 1.25rem;
        }
      }
    }
  }

  &__update-yield-wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    margin-bottom: 30px;

    @media (min-width: 500px) {
      align-items: center;
      flex-wrap: nowrap;
    }

    .button-text-wrapper {
      width: 100%;

      .adjust-advantage {
        margin-bottom: 1.5rem;
      }

      @media (min-width: 500px) {
        margin-left: 1.5rem;
        width: auto;

        .adjust-advantage {
          margin-bottom: 0.3125rem;
        }
      }
    }
  }

  .mx-2 {
    margin: 0 1rem;
  }

  .prepend {
    font-size: 2.125rem;
    left: -4px;
    position: relative;
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    display: none !important; /* stylelint-disable-line declaration-no-important */
  }

  input.numeric-input {
    font-family: $articulat-cf;
    font-weight: 700;
    border: 1px solid rgba(66, 65, 65, 0.3);
    border-radius: 4px;
    color: color('black');
    font-size: 2.125rem;
    margin-right: .5rem;
    max-height: 36px;
    position: relative;
    width: 100px;
    &::placeholder {
      color: color('black');
      opacity: 1;
    }

    &:focus::placeholder {
      color: transparent;
    }
  }

  :deep(&__slider) {
    margin-bottom: spacing('sm', 1);
    margin-top: spacing('sm', 1);

    .vue-slider-rail {
      background-color: shade('white', 10%);
    }

    .vue-slider-process {
      background-color: color('microessentials-yellow');
      border-radius: 0;
    }

    .vue-slider-dot-handle {
      border: solid 2px color('microessentials-yellow');
      box-shadow: none;
    }
  }

  &__add-col {
    padding: spacing('lg') 0;
    text-align: center;

    &--bordered {
      border-bottom: dashed 1.5px shade('white', 10%);

      @media (min-width: 380px) {
        border-right: dashed 1.5px shade('white', 10%);
        border-top: dashed 1.5px shade('white', 10%);
        width: 50%;
      }

      &:last-child {
        border-right: 0;
      }
    }
    :deep(h4) {
      color: color('microessentials-yellow');
    }
  }

  &__download-container {
    align-items: center;
    display: flex;
    flex-direction: column;
    margin-block: spacing('lg');
    margin-bottom: spacing('lg');
  }

  &__bubble_button_wrapper {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    margin-bottom: 2.7rem;

    @include breakpoint('md') {
      justify-content: center;
    }

    &.short-wrapper {
      margin-bottom: 0.65rem;
    }

    &.centered-wrapper {
      justify-content: center;
    }

    &.desk-width {
      @media (min-width: 570px) {
        justify-content: center;
      }
    }
  }

  &__bubble_button {
    align-items: center;
    border: solid 2px shade('white', 10%);
    border-radius: 50%;
    color: color('black');
    display: flex;
    font-size: 0.9375rem;
    font-weight: 700;
    height: 5rem;
    justify-content: center;
    line-height: 1;
    margin: spacing('sm') 0.75rem;
    padding: 0 spacing('sm');
    text-align: center;
    width: 5rem;

    @include breakpoint('md') {
      height: 4rem;
      margin: spacing('sm') 0.6rem;
      width: 4rem;
    }

    &--unit {
      border-radius: 17% / 50%;
      font-size: 0.9375rem;
      margin: 0.625rem 1rem;
      padding: 11px 0;
      text-transform: uppercase;

      @media (min-width: 570px) {
        margin: 0.625rem 0.6rem;
      }
    }

    &--long {
      border-radius: 10% / 50%;
      height: auto;
      max-width: 20rem;
      min-width: 15rem;
      padding: 11px 0;
    }

    &--selected {
      border-color: color('microessentials-yellow');
      color: color('microessentials-yellow');
    }

    input {
      display: none;
    }

    span {
      text-transform: uppercase;
    }
  }

  &__bubble_image {
    width: 1.75rem;
  }

  &__yield-btn {
    margin: 0;
    max-height: 55.2px;

    &.btn-inline {
      @include breakpoint('sm') {
        margin: 0;
      }
    }
  }

  &__download-btn {
    margin-bottom: 1.4rem;
    margin-top: 3.5rem;
    border-radius: 70px;

    :deep(.button__icon) {
      color: color('microessentials-yellow');
      position: relative;
      top: -3px;
    }

    &.adjust-padding {
      padding: 0.6rem 1.3125rem 0.6rem;
    }
  }

  &__send-btn {
    margin-bottom: 2rem;
  }

  &__calc-btn,
  &__download-btn,
  &__print-btn,
  &__send-btn,
  &__yield-btn {
    border: 1px solid color('microessentials-yellow');
    color: color('black');
    display: flex;
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    min-width: 250px;
    padding: 1.1rem 1.3125rem 1rem;
    transition: background-color 0.2s ease-out, color 0.2s ease-out;

    &.button {
      display: flex;
    }

    :deep(.button__icon) {
      color: color('microessentials-yellow');
    }

    &:hover {
      background-color: color('microessentials-yellow');
      color: color('white');
      transition: background-color 0.2s ease-in, color 0.2s ease-in;

      :deep(.button__icon) {
        color: color('white');
      }
    }
  }

  &__results-heading {
    &.heading--sm {
      font-size: 0.9375rem;
    }
  }

  &__results-large-heading {
    @include breakpoint('md') {
      font-size: 84px;
    }
  }

  &__sample-blend {
    &--button {
      margin-top: 1rem;

      p {
        color: color('gray');
        font-size: 0.8125rem;
        text-decoration: underline;
      }
    }

    &--container {
      margin-bottom: 2rem;

      ul {
        padding: 16px 0 0 20px;
      }
      li {
        list-style-type: disc;
        margin-bottom: .625rem;
      }
    }

    &--table {
      @include font('open-sans', 'normal');
      border: 1px color('light') solid;
      border-collapse: collapse;
      width: 100%;

      td {
        vertical-align: middle;
      }
    }

    &--header {
      .blend-title {
        background-color: color('dark');
        border-bottom: 2px color('light') solid;
        color: color('white');
        height: 5rem;

        &:nth-child(1),
        &:nth-child(2) {
          border-right: 2px color('light') solid;
        }
      }
    }

    &--row {
      height: 3rem;
      &:nth-child(odd) {
        background-color: color('light');
      }

      .blend-item:nth-child(even) {
        background-color: color('microessentials-yellow');
      }
    }

    &--logo img {
      width: 120px;
    }

    &--blend-title strong {
      font-weight: bold;
    }

    &--result {
      font-weight: bold;
    }
  }

  &__nutrient-summary {
    &--button {
      margin-top: 3rem;
    }

    &--container {
      margin-bottom: 2rem;
    }

    &--table {
       @include font('open-sans', 'normal');
      border: 1px color('light') solid;
      border-collapse: collapse;
      width: 100%;

      td {
        vertical-align: middle;
      }

      > thead {
        td {
          border-bottom: 2px color('light') solid;
          height: 5rem;
          &:nth-child(1) {
            width: 50%;
          }

          &:nth-child(2) {
            background-color: color('microessentials-yellow');
            width: 50%;
          }
        }
      }

      > tbody > tr {
        height: 3rem;
        &:nth-child(even) {
          background-color: color('light');
        }
      }
    }

    &--logo img {
      width: 120px;
    }

    &--blend-title strong {
      font-weight: bold;
    }

    &--result {
      font-weight: bold;
    }
  }
}

.personalized-modal {
  input {
    display: inline-block;
    width: 70%;
    padding: 8px;
    margin-bottom: 10px;
    font-family: "articulat-cf", helvetica, arial, sans-serif;
    font-weight: 700;
    border: 1px solid rgba(66, 65, 65, 0.3);
    border-radius: 4px;
    color: var(--color-black);
    font-size: 1rem;
  }

  span {
    display: inline-flex;
    width: 30%;
    margin-bottom: 7px;
  }
}

.crop-calculator-yellow-block {
    display: block;
    width: 390px;
    padding: 17px 9px;
    background-color: #F5A92B;
    color: #FFF;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 15px;
    text-align: left;
    position: relative;

    &::before {
    display: block;
    content: '';
    width: 0;
    height: 0;
    border-left: 10px solid transparent;
    border-right: 10px solid transparent;
    border-bottom: 10px solid #F5A92B;
    position: absolute;
    top: -9px;
    left: 10%;
  }
}

.formula-list {
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  margin: 0 auto;
  max-width: 333px;

  @include breakpoint('md') {
    justify-content: center;
  }

  .formula-button {
    align-items: center;
    border-radius: 50%;
    display: flex;
    flex-direction: column;
    margin: 0.3125rem;
    position: relative;
    text-align: center;
    width: 5rem;

    @include breakpoint('md') {
      margin: 0.3125rem 0;
    }

    .formula-icon {
      align-items: center;
      display: inline-flex;
      justify-content: center;

      &--border {
        border: 1.5px solid color('gray');
        border-radius: 50%;
        height: 5rem;
        width: 5rem;

        @include breakpoint('md') {
          height: 4rem;
          width: 4rem;
        }
      }
      .icon-sz {
        left: 2px;
        max-height: 22px;
        position: relative;
      }

      .icon-st,
      .icon-sf {
        left: 3px;
        position: relative;
      }

      &--image {
        color: color('black');
        width: 75%;
      }

      &--transparent {
        background-color: transparent;
        border-color: transparent;
      }

      &--white {
        background-color: color('white');
        border-color: color('gray');
        color: color('black');
      }

      &--primary {
        background-color: color('microessentials-yellow');
        border-color: color('microessentials-yellow');
        color: color('black');
      }
    }

    &.is-selected {
      .formula-icon {
        &--border {
          border: 2px solid color('microessentials-yellow');
        }

        &--image {
          color: color('microessentials-yellow');
        }
      }
    }
  }
}
.progress-circle {
  margin: 0 auto;
  max-width: 27rem;
  padding: 0 1rem 1.5rem;

  @include breakpoint('md') {
    max-width: 37.5rem;
  }
}

:deep(.progress-circle__slot) {
  h3 {
    color: color('black');
    font-size: 1.25rem;

    @include breakpoint('md') {
      font-size: 2.125rem;
      margin-bottom: 1.2rem;
    }
  }

  h4 {
    color: color('microessentials-yellow');
    font-size: 2.625rem;

    @include breakpoint('md') {
      font-size: 3.75rem;
      margin-bottom: 1.2rem;
    }
  }
}

:deep(.progress-circle__svg) {
  .progress-circle__segment {
    stroke: color('microessentials-yellow');
  }
}
</style>
